import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Search, X } from 'lucide-react'
import type { Column, RowData } from '@tanstack/react-table'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import type { FilterValue } from '@/shared/lib/api/api-types'

interface TextFilterProps<TData, TValue> {
  column: Column<TData, TValue>
  onClose: () => void
}

const TEXT_TYPES = [
  { value: 'contains', labelKey: 'dataTable.operators.contains' },
  { value: 'equals', labelKey: 'dataTable.operators.equals' },
  { value: 'startsWith', labelKey: 'dataTable.operators.startsWith' },
  { value: 'endsWith', labelKey: 'dataTable.operators.endsWith' },
  { value: 'notContains', labelKey: 'dataTable.operators.notContains' },
]

export function TextFilter<TData, TValue>({ column, onClose }: TextFilterProps<TData, TValue>) {
  const { t } = useTranslation()
  const current = column.getFilterValue() as Extract<FilterValue, { filterType: 'text' }> | undefined
  // Columns can restrict the operators to what their backend supports
  // (e.g. exact-match-only APIs set filterTextTypes: ['equals']).
  const allowed = column.columnDef.meta?.filterTextTypes
  const types = TEXT_TYPES.filter((option) => !allowed || allowed.includes(option.value))
  const [type, setType] = useState(current?.type ?? types[0]?.value ?? 'contains')
  const [value, setValue] = useState(current?.filter ?? '')

  function apply() {
    if (value.trim() === '') {
      column.setFilterValue(undefined)
    } else {
      column.setFilterValue({ filterType: 'text', type, filter: value.trim() })
    }
    onClose()
  }

  function clear() {
    column.setFilterValue(undefined)
    onClose()
  }

  return (
    <div className="flex flex-col gap-2 p-2 w-52">
      {types.length === 1 ? (
        <span className="text-xs text-muted-foreground px-1">
          {types[0] ? t(types[0].labelKey) : null}
        </span>
      ) : (
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {types.map((option) => (
              <SelectItem key={option.value} value={option.value} className="text-xs">
                {t(option.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Input
        className="h-7 text-xs"
        placeholder={t('dataTable.filterValue')}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && apply()}
        autoFocus
      />
      <div className="flex gap-1">
        <Button size="sm" className="h-6 flex-1 text-xs" onClick={apply}>
          <Search className="mr-1 h-3 w-3" /> {t('dataTable.apply')}
        </Button>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={clear}>
          <X className="h-3 w-3" />
        </Button>
      </div>
    </div>
  )
}

// Required for module augmentation to compile without error
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?: 'text' | 'number' | 'date' | 'set' | 'boolean'
    /** Restricts the text-filter operator list (e.g. ['equals'] for APIs that
     * only support exact match). Omitted = all operators. With a single
     * operator the select is replaced by a static label. */
    filterTextTypes?: string[]
    filterFetchOptions?: () => Promise<string[]>
    filterStaticOptions?: string[]
    /** When true, the `set` filter behaves as single-select (replaces the
     * current selection and hides the "All" action). Defaults to multi-select. */
    filterSingle?: boolean
    /** Maps a raw `set` filter option value to its display label. Use when the
     * filter value must be a stable identifier that differs from what should
     * be shown to the user (e.g. filtering by an id while displaying a name
     * that can be null). Defaults to showing the raw value as-is. */
    filterOptionLabel?: (value: string) => string
    align?: 'left' | 'right' | 'center'
    defaultHidden?: boolean
    /** Native tooltip of the column title, e.g. the technical name behind a translated label. */
    headerTooltip?: string
  }
}
